import isEmpty from 'lodash/isEmpty'
import queryString, { ParseOptions } from 'query-string'
import { IQuery, IDecoratedQuery, FilterOptions } from '../types/search'
import { ITypeaheadObject } from '../types/pack'
import { IOption } from '../types/common'
import { safePost } from './http'
import { packUrl } from './pack'

export const adaptQuery = ({ sluglines, status, ...query }: IQuery) => ({
  ...query,
  sluglines: (sluglines && [sluglines]) || [],
  status: status && status.toUpperCase(),
})

const transformIntoTopicCodes = (value: ITypeaheadObject[]): string[] => {
  const topics: ITypeaheadObject[] =
    typeof value === 'string' ? JSON.parse(value) : value
  return topics.map(({ rcsId }) => rcsId)
}

const adaptTopicCodes = (topicCodes?: ITypeaheadObject[]) =>
  topicCodes ? { topicCodes: transformIntoTopicCodes(topicCodes) } : {}

export const decoratePublishedSearchQuery = ({
  take = 20,
  orderBy = 'publishedDate',
  advancedQuery = false,
  topicCodes,
  ...query
}: IQuery): IDecoratedQuery => ({
  take,
  orderBy,
  advancedQuery,
  ...adaptTopicCodes(topicCodes),
  ...query,
})

export const decorateSearchQuery = ({
  take = 20,
  orderBy = 'modified',
  currentUser = true,
  advancedQuery = false,
  status = 'any',
  topicCodes,
  ...query
}: IQuery): IDecoratedQuery => ({
  take,
  orderBy,
  currentUser,
  advancedQuery,
  status,
  ...adaptTopicCodes(topicCodes),
  ...query,
})

export const adaptSearchQuery = ({
  date,
  dateTo,
  dateFrom,
  ...query
}: IQuery): IQuery => {
  const dateFilters =
    date === FilterOptions.expiryDate
      ? 'expiring'
      : date === FilterOptions.nextScheduleDate
      ? 'scheduled'
      : 'modified'
  const hasDates = !!(dateTo && dateFrom)
  return {
    ...(hasDates && { [dateFilters]: { from: dateFrom, to: dateTo } }),
    ...(date === FilterOptions.nextScheduleDate &&
      !hasDates && { scheduled: {} }),
    ...(date === FilterOptions.expiryDate && !hasDates && { expiring: {} }),
    ...query,
  }
}

export const transformIntoTag = (value = [] as string[]): IOption[] =>
  typeof value === 'string'
    ? [{ value, label: value }]
    : value.map((label) => ({ value: label, label }))

const transformSelectValue = (value) =>
  typeof value === 'string' ? [value] : value

export const parseNestedQuery = (
  searchLocation: string,
  options: ParseOptions = { parseBooleans: true }
): IQuery => {
  const query = getParams(searchLocation, options)
  Object.entries(query).forEach(([key, value]) => {
    if (typeof value === 'string' && value !== null) {
      if (key === 'topicCodes' || key === 'tags') {
        query[key] = transformSelectValue(JSON.parse(value))
      }
    }
  })
  return query
}

export const searchUrl = `${packUrl}/search`

export const search = (query: IQuery): Promise<any> =>
  safePost(searchUrl, adaptQuery(query))

export const getParams = (
  searchLocation: string,
  options: ParseOptions = { parseBooleans: true }
): IQuery => queryString.parse(searchLocation, options)

export const stringifyNestedQuery = (filters: IQuery): IQuery => {
  const query = { ...filters }
  Object.entries(query).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      query[key] = JSON.stringify(value)
    }
  })
  return query
}

// TODO: add the functionality of buildDefaultQuery into decoratePublishedSearchQuery and decorateSearchQuery
export const buildDefaultQuery = (
  isPublishingArea: boolean,
  keywords: string
): string => {
  const query: IQuery = {
    orderBy: isPublishingArea ? 'modified' : 'publishedDate',
    advancedQuery: false,
    ...(isPublishingArea ? { currentUser: true } : { includeMedia: false }),
    keywords,
  }

  return Object.entries(query)
    .filter(([key, val]) => typeof val === 'boolean' || !!val)
    .map(([key, val]) => `${key}=${val}`)
    .join('&')
}

export const removeEmptyFilters = (query: IQuery): IQuery => {
  const filteredQuery = { ...query }
  const nullableFields = [
    'tags',
    'publishedFrom',
    'publishedTo',
    'dateFrom',
    'dateTo',
    'modified',
    'sluglines',
    'graphicType',
    'topicCodes',
    'keywords',
  ]
  Object.entries(filteredQuery).forEach(([key, value]) => {
    if (nullableFields.includes(key) && isEmpty(value)) {
      delete filteredQuery[key]
    }
  })
  return filteredQuery
}

export const dateCaptions = {
  [FilterOptions.modified]: 'Modified date',
  [FilterOptions.expiryDate]: 'Expiry date',
  [FilterOptions.nextScheduleDate]: 'Scheduled date',
}
